<template>
  <v-container
    fluid>
    <v-row class="table-detail">
      <v-col
        cols="12"
        :md="selectItem ? '8' : '12'">
        <p class="text-h5">
          Access Logs
        </p>
        <div class="d-flex justify-end align-end">
          <div>
            <date-range-picker
              v-model="queryDates"
              dense
              :show-prepend-icon="false"
              outlined
              @input="getData()" />
          </div>
          <autocomplete-form
            v-model="userSearch"
            :items="users"
            item-text="name"
            label="Select User"
            class="mr-2"
            placeholder="Select"
            name="Users"
            clearable
            dense
            return-object
            outlined
            hide-details
            @change="getData()"
            @search="searchUser" />
          <div>
            <v-autocomplete
              ref="searchTextField"
              v-model.trim="inputSearch"
              outlined
              clearable
              dense
              :items="reportList"
              label="Report Name"
              class="search-text-field mr-2"
              hide-details
              @change="getData" />
          </div>
          <v-btn
            dark
            @click="getData()">
            Search
          </v-btn>
        </div>
        <v-divider class="mt-4"></v-divider>
        <div
          class="d-flex flex-wrap my-4">
          <v-chip
            v-for="(tag, i) in categories"
            :key="`tag-${tag}-${i}`"
            class="mr-1 mt-1 cs-pointer"
            :outlined="!selectedCategories.includes(tag.text)"
            :text-color="!selectedCategories.includes(tag.text) ? '' : 'white'"
            :color="tag.color"
            small
            @click="selectCategory(tag.text)">
            {{ tag.text }}
          </v-chip>
        </div>
        <v-divider class="mt-4"></v-divider>
        <v-data-table
          :headers="header"
          :items="items"
          :options.sync="options"
          :items-per-page="options.itemsPerPage"
          :server-items-length="total"
          :loading="loading"
          :search="inputSearch"
          :footer-props="{'items-per-page-options': [15, 25, 50, 100]}"
          class="w-full"
          @update:options="getData()"
        >
          <template #[`item.createdAt`]="{ item }">
            {{ item.createdAt | dateTimeFormat() }}
          </template>
          <template #[`item.category`]="{ item }">
            <div
              class="d-flex flex-wrap">
              <v-chip
                v-for="(category, index) in item.category"
                :key="index"
                small
                text-color="white"
                :color="getColor(category)"
                class="mr-1 mt-1 cs-pointer"
                @click="selectCategory(category)">
                {{ category }}
              </v-chip>
            </div>
          </template>
          <template #[`item.description`]="{ item }">
            <span
              class="text-bold"
              :style="getStatusStyle(item.httpStatus)">{{ item.httpStatus }}</span> {{ item.description }}
          </template>
          <template #[`item.option`]="{ item }">
            <v-btn
              v-if="!selectItem || selectItem.id !== item.id"
              link
              icon
              x-small
              @click="showItemDetail(item)">
              <v-icon>
                mdi-magnify
              </v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-col>
      <v-col
        v-if="selectItem"
        cols="12"
        md="4"
        class="pa-0">
        <div class="log-detail">
          <div class="d-flex justify-space-between align-center">
            <h3 class="text-bold mb-0">
              {{ selectItem.name }}
            </h3>
            <v-btn
              icon
              @click="selectItem = null">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
          <p class="mb-0 mt-2">
            <b>Status Code </b>
            <span
              :style="getStatusStyle(selectItem.statusCode)"
              class="text-bold">{{ selectItem.statusCode }}</span>
          </p>
          <p class="mb-0">
            <b>Date </b>{{ selectItem.createdAt | dateTimeFormat() }}
          </p>
          <p class="mb-0">
            <b>Category </b>
          </p>
          <div
            class="d-flex flex-wrap">
            <v-chip
              v-for="(category, index) in selectItem.category"
              :key="index"
              small
              class="mr-1 mt-1 cs-pointer"
              color="secondary">
              {{ category }}
            </v-chip>
          </div>
          <div class="d-flex flex-wrap mt-4 custom-gap">
            <a
              @click="scrollTo('Response')">#Response</a>
            <a
              @click="scrollTo('User')">#User</a>
            <a
              @click="scrollTo('URLs')">#URLs</a>
            <a
              @click="scrollTo('Headers')">#Headers</a>
            <a
              v-if="selectItem.body"
              @click="scrollTo('Body')">#Body</a>
            <a
              v-if="selectItem.query"
              @click="scrollTo('Params')">#Params</a>
            <a
              v-if="selectItem.query"
              @click="scrollTo('Query')">#Query</a>
            <a
              @click="scrollTo('Store')">#Store</a>
          </div>
          <p
            ref="Response"
            class="mt-4">
            Response
          </p>
          <div style="border: 1px solid #000;">
            <json-viewer
              :value="selectItem.responseData"
              :expand-depth="5"
              copyable
              sort />
          </div>
          <p
            ref="User"
            class="mt-4">
            User
          </p>
          <div style="border: 1px solid #000;">
            <json-viewer
              :value="selectItem.user"
              :expand-depth="5"
              copyable
              sort />
          </div>
          <p
            ref="URLs"
            class="mt-4">
            URLs
          </p>
          <div style="border: 1px solid #000;">
            <json-viewer
              :value="{ method: selectItem.method, path: selectItem.path, fullPath: selectItem.fullPath }"
              :expand-depth="1"
              copyable />
          </div>
          <p
            ref="Headers"
            class="mt-4">
            Headers
          </p>
          <div
            style="border: 1px solid #000;">
            <json-viewer
              :value="selectItem.headers"
              :expand-depth="1"
              copyable />
          </div>
          <p
            v-if="selectItem.body"
            ref="Body"
            class="mt-4">
            Body
          </p>
          <div
            v-if="selectItem.body"
            style="border: 1px solid #000;">
            <json-viewer
              :value="selectItem.body"
              :expand-depth="5"
              copyable
              sort />
          </div>
          <p
            v-if="selectItem.params"
            ref="Params"
            class="mt-4">
            Params
          </p>
          <div
            v-if="selectItem.params"
            style="border: 1px solid #000;">
            <json-viewer
              :value="selectItem.params"
              :expand-depth="5"
              copyable
              sort />
          </div>
          <p
            v-if="selectItem.query"
            ref="Query"
            class="mt-4">
            Query
          </p>
          <div
            v-if="selectItem.query"
            style="border: 1px solid #000;">
            <json-viewer
              :value="selectItem.query"
              :expand-depth="5"
              copyable
              sort />
          </div>
          <p
            ref="Store"
            class="mt-4">
            Store
          </p>
          <div style="border: 1px solid #000;">
            <json-viewer
              :value="selectItem.store"
              :expand-depth="5"
              copyable
              sort />
          </div>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import AccessLogsProvider from '@/resources/AccessLogProvider'
import UserProvider from '@/resources/UserProvider'
import DateRangePicker from '@/components/DateRangePicker.vue'

export default {
  components: {
    DateRangePicker
  },
  data () {
    return {
      userService: new UserProvider(),
      accessLogsService: new AccessLogsProvider(),
      inputSearch: '',
      loading: false,
      selectItem: null,
      showDetail: false,
      total: 0,
      userSearch: null,
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['id'],
        sortDesc: [true]
      },
      header: [
        {
          text: 'Name',
          value: 'name'
        },
        {
          text: 'Date',
          value: 'createdAt'
        },
        {
          text: 'User',
          value: 'user.email'
        },
        {
          text: 'Category',
          value: 'category'
        },
        {
          text: 'Options',
          value: 'option',
          align: 'center'
        }
      ],
      items: [],
      users: [],
      queryDates: [this.$dayjs().startOf('day').format('YYYY-MM-DD'), this.$dayjs().endOf('day').format('YYYY-MM-DD')],
      colors: [
        '#4caf50',
        '#2196f3',
        '#ff9800',
        '#f44336',
        '#9c27b0',
        '#673ab7',
        '#3f51b5',
        '#03a9f4',
        '#009688',
        '#ff5722',
        '#795548',
        '#607d8b',
        '#e91e63',
        '#1B5E20'
      ],
      categories: [],
      selectedCategories: [],
      searchTimeoutId: null,
      reportList: []
    }
  },
  watch: {
    userSearch (newValue) {
      if (newValue) {
        this.getData()
      }
    }
  },
  created () {
    this.getData()
    this.fetchUser()
  },
  methods: {
    async searchUser (searchText) {
      if (this.searchTimeoutId) clearTimeout(this.searchTimeoutId)
      this.searchTimeoutId = setTimeout(async () => {
        this.fetchUser(searchText)
      }, 500)
    },
    async fetchUser (searchText) {
      const { data } = await this.userService.getUsers({
        page: 1,
        itemsPerPage: 10,
        sortBy: ['id'],
        sortDesc: [true],
        search: searchText,
        role: this.selectedRoleCompute
      })
      this.users = data.results
    },

    showItemDetail (item) {
      this.selectItem = item
      this.showDetail = true
    },
    scrollTo (tag) {
      const el = this.$refs[tag]
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' })
      }
    },
    getStatusStyle (status) {
      if (+status >= 200 && +status < 300) {
        return 'color: #4CAF50;'
      }
      if (+status < 600) {
        return 'color: #F44336'
      }

      return ''
    },
    getCategoryText (category) {
        return category.length ? category.join(', ') : '-'
    },
    async getData (clear = false) {
      if (clear) {
        this.options.page = 1
      }
      this.selectItem = null
      try {
        this.loading = true
        const { data: { results, total, logCategories, reportList } } = await this.accessLogsService.getAccessLogs(
          {
            page: this.options.page,
            limit: this.options.itemsPerPage,
            sortBy: this.options.sortBy[0],
            sortOrder: this.options.sortDesc[0] ? 'desc' : 'asc',
            search: this.inputSearch,
            categories: JSON.stringify(this.selectedCategories),
            user: this.userSearch?.email ?? '',
            startDate: this.queryDates[0] ? this.$dayjs(this.queryDates[0]).startOf('day') : null,
            endDate: this.queryDates[1] ? this.$dayjs(this.queryDates[1]).endOf('day') : null
          }
        )
        this.mapCategories(logCategories)
        this.reportList = reportList
        this.items = results
        this.total = total
      } catch (error) {
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: `Error ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    mapCategories (categories = []) {
      this.categories = categories.map((category, i) => ({
        text: category,
        color: this.colors[i % this.colors.length]
      }))
    },
    selectCategory (cat) {
      if (this.selectedCategories.includes(cat)) {
        this.selectedCategories = this.selectedCategories.filter((c) => c !== cat)
      } else {
        this.selectedCategories.push(cat)
      }
      this.getData()
    },
    getColor (category) {
      return this.categories.find((c) => c.text === category)?.color || 'secondary'
    }
  }
}
</script>
<style scoped>
.table-detail {
  height: calc(100vh - 80px);
  overflow-y: auto;
  background-color: #fff;
}
.log-detail {
  width: 100%;
  margin-left: 10px;
  padding: 10px;
  height: 100%;
  overflow-y: auto;
  height: calc(100vh - 80px);
  background-color: #fff;
}
.custom-gap {
  gap: 0.5rem 1rem;
}
</style>
